import { useState } from "react";
import { 
  Flame, Beef, Apple, Droplets, Settings, Plus, History, 
  ChevronDown, ChevronUp, TrendingUp, ScanBarcode, Check, RotateCcw, Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { format, parseISO, isToday, isYesterday } from "date-fns";
import { NutritionGoalsSetup } from "@/components/NutritionGoalsSetup";
import { BarcodeScanner } from "@/components/BarcodeScanner";
import type { DailyNutritionSummary, NutritionLog } from "@/hooks/useNutritionLogs";
import type { NutritionGoalType } from "@/hooks/useProfile";
import type { NutritionEntry } from "@/components/NutritionLogger";

interface NutritionHubProps {
  // Progress data
  consumed: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  targets: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    goalType?: NutritionGoalType;
  };
  // Logging
  onLogNutrition: (entry: NutritionEntry) => Promise<boolean>;
  isHealthConnected?: boolean;
  healthPlatformName?: string;
  // Goals setup
  userStats?: {
    weight?: number;
    height?: string;
    age?: number;
    sex?: string;
    activityLevel?: string;
  };
  onSaveNutritionGoals?: (targets: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    goalType: NutritionGoalType;
  }) => Promise<void>;
  // History
  nutritionLogs?: NutritionLog[];
  onRepeatMeal?: (log: NutritionLog) => Promise<boolean>;
  onDeleteNutritionLog?: (logId: string) => Promise<boolean>;
  // Weekly data
  weekNutrition?: DailyNutritionSummary[];
  className?: string;
}

const MEAL_PRESETS = [
  { name: 'Breakfast', icon: '🍳', defaultCals: 400 },
  { name: 'Lunch', icon: '🥗', defaultCals: 600 },
  { name: 'Dinner', icon: '🍽️', defaultCals: 700 },
  { name: 'Snack', icon: '🍎', defaultCals: 200 },
];

export function NutritionHub({
  consumed,
  targets,
  onLogNutrition,
  isHealthConnected,
  healthPlatformName,
  userStats,
  onSaveNutritionGoals,
  nutritionLogs = [],
  onRepeatMeal,
  onDeleteNutritionLog,
  weekNutrition,
  className,
}: NutritionHubProps) {
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [calories, setCalories] = useState('');
  const [protein, setProtein] = useState('');
  const [carbs, setCarbs] = useState('');
  const [fat, setFat] = useState('');
  const [selectedMeal, setSelectedMeal] = useState<string | null>(null);
  const [isLogging, setIsLogging] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const hasGoalsSet = targets.calories > 0;
  const caloriePercent = hasGoalsSet 
    ? Math.min((consumed.calories / targets.calories) * 100, 100) 
    : 0;
  const caloriesRemaining = Math.max(targets.calories - consumed.calories, 0);
  const isOverCalories = consumed.calories > targets.calories;

  const macros = [
    { 
      name: 'Protein', 
      consumed: consumed.protein, 
      target: targets.protein, 
      icon: Beef, 
      color: 'text-red-500',
      bgColor: 'bg-red-500',
    },
    { 
      name: 'Carbs', 
      consumed: consumed.carbs, 
      target: targets.carbs, 
      icon: Apple, 
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500',
    },
    { 
      name: 'Fat', 
      consumed: consumed.fat, 
      target: targets.fat, 
      icon: Droplets, 
      color: 'text-blue-500',
      bgColor: 'bg-blue-500',
    },
  ];

  // Today's logs for quick view
  const todayLogs = nutritionLogs.filter(log => log.logged_date === format(new Date(), 'yyyy-MM-dd'));

  const handleProductScanned = (product: {
    calories: number;
    protein_g: number;
    carbs_g: number;
    fat_g: number;
  }) => {
    setCalories(product.calories.toString());
    setProtein(product.protein_g.toString());
    setCarbs(product.carbs_g.toString());
    setFat(product.fat_g.toString());
    setSelectedMeal('Snack');
  };

  const handleQuickLog = async (meal: typeof MEAL_PRESETS[0]) => {
    setIsLogging(true);
    const success = await onLogNutrition({
      calories: meal.defaultCals,
      meal: meal.name,
      timestamp: new Date().toISOString(),
    });
    setIsLogging(false);
    
    if (success) {
      toast.success(`${meal.name} logged!`, {
        description: isHealthConnected ? `Synced to ${healthPlatformName}` : undefined,
      });
    }
  };

  const handleSubmit = async () => {
    const cals = parseInt(calories);
    if (!cals || cals <= 0) {
      toast.error('Please enter valid calories');
      return;
    }

    setIsLogging(true);
    const success = await onLogNutrition({
      calories: cals,
      protein: protein ? parseInt(protein) : undefined,
      carbs: carbs ? parseInt(carbs) : undefined,
      fat: fat ? parseInt(fat) : undefined,
      meal: selectedMeal || undefined,
      timestamp: new Date().toISOString(),
    });
    setIsLogging(false);

    if (success) {
      toast.success('Nutrition logged!', {
        description: isHealthConnected ? `Synced to ${healthPlatformName}` : undefined,
      });
      setCalories('');
      setProtein('');
      setCarbs('');
      setFat('');
      setSelectedMeal(null);
      setLogDialogOpen(false);
    }
  };

  const handleRepeat = async (log: NutritionLog) => {
    if (!onRepeatMeal) return;
    setIsLogging(true);
    await onRepeatMeal(log);
    setIsLogging(false);
  };

  const handleDelete = async (logId: string) => {
    if (!onDeleteNutritionLog) return;
    setIsLogging(true);
    await onDeleteNutritionLog(logId);
    setDeleteConfirm(null);
    setIsLogging(false);
  };

  const openSetupModal = () => {
    const setupEl = document.getElementById('nutrition-goals-setup-trigger');
    if (setupEl) setupEl.click();
  };

  // No goals set state
  if (!hasGoalsSet) {
    return (
      <div className={cn("glass-card rounded-2xl p-5", className)}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Flame className="w-5 h-5 text-primary" />
            Nutrition
          </h3>
          {onSaveNutritionGoals && (
            <NutritionGoalsSetup
              currentTargets={targets}
              userStats={userStats}
              onSave={onSaveNutritionGoals}
              trigger={<button id="nutrition-goals-setup-trigger" className="hidden" />}
            />
          )}
        </div>
        <div className="text-center py-6 space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <Flame className="w-8 h-8 text-primary" />
          </div>
          <div>
            <p className="text-muted-foreground text-sm mb-3">
              Set your nutrition goals to track daily progress
            </p>
            {onSaveNutritionGoals && (
              <Button onClick={openSetupModal} variant="default" className="gap-2">
                <Settings className="w-4 h-4" />
                Set Goals
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("glass-card rounded-2xl overflow-hidden", className)}>
      {/* Header with settings */}
      <div className="flex items-center justify-between p-5 pb-4">
        <h3 className="font-display text-lg font-bold flex items-center gap-2">
          <Flame className="w-5 h-5 text-primary" />
          Nutrition
        </h3>
        <div className="flex items-center gap-2">
          {onSaveNutritionGoals && (
            <>
              <NutritionGoalsSetup
                currentTargets={targets}
                userStats={userStats}
                onSave={onSaveNutritionGoals}
                trigger={<button id="nutrition-goals-setup-trigger" className="hidden" />}
              />
              <button
                onClick={openSetupModal}
                className="p-1.5 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <Settings className="w-4 h-4 text-muted-foreground" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Calorie Progress Ring + Stats */}
      <div className="px-5 pb-4">
        <div className="flex items-center gap-6">
          <div className="relative w-24 h-24 flex-shrink-0">
            <svg className="w-24 h-24 -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="40"
                strokeWidth="10"
                fill="none"
                className="stroke-secondary"
              />
              <circle
                cx="50"
                cy="50"
                r="40"
                strokeWidth="10"
                fill="none"
                strokeLinecap="round"
                className={cn(
                  "transition-all duration-500",
                  isOverCalories ? "stroke-red-500" : "stroke-primary"
                )}
                style={{
                  strokeDasharray: `${2 * Math.PI * 40}`,
                  strokeDashoffset: `${2 * Math.PI * 40 * (1 - caloriePercent / 100)}`,
                }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn(
                "text-xl font-display font-bold",
                isOverCalories && "text-red-500"
              )}>
                {consumed.calories}
              </span>
              <span className="text-[10px] text-muted-foreground">
                / {targets.calories}
              </span>
            </div>
          </div>

          <div className="flex-1">
            <p className={cn(
              "text-2xl font-display font-bold",
              isOverCalories ? "text-red-500" : "text-foreground"
            )}>
              {isOverCalories 
                ? `+${consumed.calories - targets.calories}` 
                : caloriesRemaining}
            </p>
            <p className="text-sm text-muted-foreground">
              {isOverCalories ? 'over target' : 'calories left'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {Math.round(caloriePercent)}% of daily goal
            </p>
          </div>
        </div>

        {/* Macro Progress Bars */}
        <div className="space-y-3 pt-4 mt-4 border-t border-border/50">
          {macros.map((macro) => {
            const percent = macro.target > 0 
              ? Math.min((macro.consumed / macro.target) * 100, 100)
              : 0;
            const isOver = macro.consumed > macro.target;
            
            return (
              <div key={macro.name} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1.5">
                    <macro.icon className={cn("w-3.5 h-3.5", macro.color)} />
                    <span className="text-muted-foreground">{macro.name}</span>
                  </div>
                  <span className={cn(
                    "font-medium",
                    isOver && "text-red-500"
                  )}>
                    {macro.consumed}g / {macro.target}g
                  </span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className={cn(
                      "h-full rounded-full transition-all duration-300",
                      isOver ? "bg-red-500" : macro.bgColor
                    )}
                    style={{ width: `${Math.min(percent, 100)}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Log + Actions */}
      <div className="px-5 pb-4">
        <div className="flex items-center gap-2">
          <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex-1 gap-2">
                <Plus className="w-4 h-4" />
                Log Food
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Apple className="w-5 h-5 text-primary" />
                  Log Nutrition
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {/* Barcode scanner button */}
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => setScannerOpen(true)}
                >
                  <ScanBarcode className="w-4 h-4" />
                  Scan Barcode
                </Button>

                {/* Quick meal presets */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Quick Log</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {MEAL_PRESETS.map((meal) => (
                      <button
                        key={meal.name}
                        onClick={() => handleQuickLog(meal)}
                        disabled={isLogging}
                        className={cn(
                          "flex flex-col items-center gap-1 p-3 rounded-xl border transition-all",
                          "hover:bg-primary/10 hover:border-primary/50",
                          "disabled:opacity-50 disabled:cursor-not-allowed"
                        )}
                      >
                        <span className="text-xl">{meal.icon}</span>
                        <span className="text-xs font-medium">{meal.name}</span>
                        <span className="text-[10px] text-muted-foreground">{meal.defaultCals} cal</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">or custom</span>
                  </div>
                </div>

                {/* Custom entry */}
                <div className="space-y-3">
                  {/* Meal type selection */}
                  <div className="flex gap-2">
                    {MEAL_PRESETS.map((meal) => (
                      <button
                        key={meal.name}
                        onClick={() => setSelectedMeal(selectedMeal === meal.name ? null : meal.name)}
                        className={cn(
                          "flex-1 py-1.5 px-2 text-xs rounded-lg border transition-all",
                          selectedMeal === meal.name
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border hover:border-primary/50"
                        )}
                      >
                        {meal.icon} {meal.name}
                      </button>
                    ))}
                  </div>

                  {/* Calories - required */}
                  <div className="space-y-1">
                    <Label htmlFor="calories" className="text-xs flex items-center gap-1">
                      <Flame className="w-3 h-3 text-orange-500" /> Calories *
                    </Label>
                    <Input
                      id="calories"
                      type="number"
                      placeholder="e.g., 500"
                      value={calories}
                      onChange={(e) => setCalories(e.target.value)}
                      className="h-9"
                    />
                  </div>

                  {/* Macros - optional */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Label htmlFor="protein" className="text-xs flex items-center gap-1">
                        <Beef className="w-3 h-3 text-red-500" /> Protein
                      </Label>
                      <Input
                        id="protein"
                        type="number"
                        placeholder="g"
                        value={protein}
                        onChange={(e) => setProtein(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="carbs" className="text-xs flex items-center gap-1">
                        <Apple className="w-3 h-3 text-yellow-500" /> Carbs
                      </Label>
                      <Input
                        id="carbs"
                        type="number"
                        placeholder="g"
                        value={carbs}
                        onChange={(e) => setCarbs(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="fat" className="text-xs flex items-center gap-1">
                        <Droplets className="w-3 h-3 text-blue-500" /> Fat
                      </Label>
                      <Input
                        id="fat"
                        type="number"
                        placeholder="g"
                        value={fat}
                        onChange={(e) => setFat(e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Health sync indicator */}
                {isHealthConnected && (
                  <div className="flex items-center gap-2 text-xs text-green-600 bg-green-500/10 px-3 py-2 rounded-lg">
                    <Check className="w-3 h-3" />
                    Will sync to {healthPlatformName}
                  </div>
                )}

                <Button 
                  onClick={handleSubmit} 
                  disabled={isLogging || !calories}
                  className="w-full"
                >
                  {isLogging ? 'Logging...' : 'Log Nutrition'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Today's Meals - Collapsible */}
      {todayLogs.length > 0 && onRepeatMeal && onDeleteNutritionLog && (
        <Collapsible open={historyExpanded} onOpenChange={setHistoryExpanded}>
          <CollapsibleTrigger asChild>
            <button className="w-full flex items-center justify-between px-5 py-3 border-t border-border/50 hover:bg-secondary/30 transition-colors">
              <div className="flex items-center gap-2 text-sm">
                <History className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">Today's Meals</span>
                <span className="text-xs text-muted-foreground">
                  ({todayLogs.length} {todayLogs.length === 1 ? 'entry' : 'entries'})
                </span>
              </div>
              {historyExpanded ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="border-t border-border/30 divide-y divide-border/30">
              {todayLogs.map((log) => (
                <div
                  key={log.id}
                  className="px-5 py-3 flex items-center justify-between"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {log.meal_type || 'Custom'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {format(parseISO(log.created_at), 'h:mm a')}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span className="flex items-center gap-1">
                        <Flame className="w-3 h-3 text-orange-500" />
                        {log.calories}
                      </span>
                      {log.protein_g && (
                        <span className="flex items-center gap-1">
                          <Beef className="w-3 h-3 text-red-500" />
                          {log.protein_g}g
                        </span>
                      )}
                      {log.carbs_g && (
                        <span className="flex items-center gap-1">
                          <Apple className="w-3 h-3 text-yellow-500" />
                          {log.carbs_g}g
                        </span>
                      )}
                      {log.fat_g && (
                        <span className="flex items-center gap-1">
                          <Droplets className="w-3 h-3 text-blue-500" />
                          {log.fat_g}g
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleRepeat(log)}
                      disabled={isLogging}
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteConfirm(log.id)}
                      disabled={isLogging}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Barcode Scanner */}
      <BarcodeScanner
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onProductFound={handleProductScanned}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this nutrition log entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
